import useAPI, {
  useAPIMutation,
  useDistinctFilterOptions,
  useMultipleAPIs,
  useResetAPI,
  createAuthRetryHandler,
  // Plain async fetch helper (not a hook despite the name) — alias so the
  // rules-of-hooks lint doesn't flag the on-demand call inside useCallback.
  useAPIHelper as fetchAPIData
} from "@/services/api/hook";
import type { QueryKeyArray } from "@/services/api/hook";
import { useAuth } from "@/services/auth/AuthContext";
import { useQueryClient } from "@tanstack/react-query";
import {
  APIObject,
  ComputedFields,
  EmptyFilters,
  ExtendedUseQueryResult,
  Filters,
  SearchQuery
} from "@/services/api/types";
import {
  AlertsBatchResponse,
  CreateQuestionnaireQuestionSettings,
  DealData,
  DealMilestonesResponse,
  DealQuestionnaireQuestionSettings,
  DealNextStepsResponse,
  DealQuestionnaireResults,
  DealQuestionnaireScores,
  DealQuestionnaireSettings,
  DealsMeResponse,
  DealsPage,
  DealsQuestionnairesResults,
  DealsQuestionnairesResultsResponse,
  DealsQuestionnairesScoresResponse,
  DealsQuestionnairesSettingsResponse,
  QuestionValueType,
  Score,
  NextStep,
  Commercials,
  DealQuestionnaireQuestionOptionsResponse,
  DealSummaryQueryResponse,
  QuestionnaireUpdateInput,
  QuestionnaireBaseOutput,
  QuestionnaireReorderResponse,
  QuestionnaireQuestionsReorderResponse,
  QuestionnaireByIdResponse
} from "./types";
import { toDealsRequestFilters } from "./helpers";
import { refreshEntityDetail, useEntities } from "@/services/api/entityhooks";
import { EntitySliceState } from "@/services/api/entitystore";
import useBoundStore, {
  MAX_ENTITIES_PER_PAGE
} from "@/services/store/BoundStore";
import { useCallback, useEffect, useMemo } from "react";
import { createUseFilteredEntitiesInfinite } from "@/services/api/infinite";
import { DealFilters } from "@/pages/deals/components/DealsToolbar";
import { PersonData } from "@/entities/people/types";

export const MAX_DEALS_PER_PAGE = MAX_ENTITIES_PER_PAGE;

export const useDeals = (
  ids: string[],
  options: {
    enabled?: boolean;
    computed_fields?: ComputedFields;
    forceRefetch?: boolean;
    include_additionals?: boolean;
  } = {}
) => {
  const useDealsStore = (): EntitySliceState<DealData> => {
    return useBoundStore((state) => state.deals);
  };

  return useEntities<DealData>(
    ids,
    "deals",
    useDealsStore,
    (endpoint, params, apiOptions) =>
      useAPI(
        endpoint,
        {
          ...params,
          queryParams: {
            ...(options.computed_fields && {
              computed_fields: options.computed_fields
            }),
            ...(options.include_additionals && {
              include_additionals: "true"
            })
          }
        },
        { ...apiOptions, enabled: options.enabled }
      ),
    undefined,
    options.computed_fields,
    options.forceRefetch
  );
};

export const useDealsMe = (): DealsMeResponse => {
  const result = useAPI("deals/me", { method: "GET" });
  return result;
};
export const useResetDealsMe = () => useResetAPI("deals/me", false);

export const useResetDeal = (dealId: string) => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({
      queryKey: ["deals", { pathParams: { dealsIds: dealId } }]
    });
  };
};

export const useAllDeals = (
  page: number = 1,
  size: number = MAX_DEALS_PER_PAGE,
  computed_fields: ComputedFields = ComputedFields.ALL
): ExtendedUseQueryResult<DealsPage> => {
  const result = useAPI("deals/all", {
    queryParams: {
      page: page.toString(),
      size: size.toString(),
      computed_fields
    }
  });

  const addDeal = useBoundStore((state) => state.deals.addEntity);

  useEffect(() => {
    if (result.data?.deals) {
      result.data.deals.forEach(addDeal);
    }
  }, [result.data, addDeal]);

  return result;
};

export const {
  useFilteredEntitiesInfinite,
  useResetFilteredEntitiesInfinite: useResetFilteredDeals
} = createUseFilteredEntitiesInfinite<DealData, DealFilters>(
  "deals",
  MAX_DEALS_PER_PAGE
);

export const useFilteredDeals = (
  filters: Filters = EmptyFilters,
  search?: SearchQuery,
  groupedBy?: keyof DealData,
  pageSize = MAX_DEALS_PER_PAGE,
  sortField?: string,
  sortAsc?: boolean,
  fetchAll = false,
  enabled = true,
  computed_fields: ComputedFields = ComputedFields.ALL,
  groupedByFunction?: string
) => {
  return useFilteredEntitiesInfinite(
    () => ({ addEntity: useBoundStore((state) => state.deals.addEntity) }),
    {
      filters: toDealsRequestFilters(filters),
      search,
      groupedBy,
      groupedByFunction,
      pageSize,
      sortField,
      sortAsc,
      fetchAll,
      enabled,
      computed_fields
    }
  );
};

export const useCreateDealMutation = () => {
  const addDeal = useBoundStore((state) => state.deals.addEntity);
  return useAPIMutation<DealData, APIObject>(
    "dealCreate",
    {
      method: "POST"
    },
    (response, data) => {
      addDeal({
        ...data,
        id: response.id
      } as DealData);
    }
  );
};

export const useUpdateDealMutation = () => {
  const updateDeal = useBoundStore((state) => state.deals.updateEntity);
  const queryClient = useQueryClient();
  return useAPIMutation<
    { pathParams: { dealId: string }; data: Partial<DealData> },
    DealData
  >(
    "deals/id",
    {
      method: "PUT"
    },
    (response) => {
      updateDeal(response.id, response);
      refreshEntityDetail(queryClient, "deals", response.id);
    }
  );
};

export const useDealMilestones =
  (): ExtendedUseQueryResult<DealMilestonesResponse> =>
    useAPI("dealMilestones");

export const useUpdateDealStageMutation = () => {
  const updateDeal = useBoundStore((state) => state.deals.updateEntity);
  const queryClient = useQueryClient();
  return useAPIMutation<
    {
      pathParams: {
        dealId: string;
        newStageId: number;
      };
      data: Record<string, never>; // empty object for the request body
    },
    DealData
  >(
    "deals/updateStage",
    {
      method: "PUT"
    },
    (response) => {
      updateDeal(response.id, {
        stage: response.stage,
        daysInStage: response.daysInStage
      });
      refreshEntityDetail(queryClient, "deals", response.id);
    }
  );
};

export const useUpdateDealStatusMutation = () => {
  const updateDeal = useBoundStore((state) => state.deals.updateEntity);
  const queryClient = useQueryClient();
  return useAPIMutation<
    {
      pathParams: {
        dealId: string;
      };
      data: {
        newStatus: number;
        closedLostReason?: number;
        closedLostReasonText?: string | null;
        contractName?: string;
        companyId?: string;
        productId?: string;
        currency?: string;
        contractSignDate?: string | null;
        aeOwner?: string | null;
        csmOwner?: string | null;
      };
    },
    DealData
  >(
    "deals/updateStatus",
    {
      method: "PUT"
    },
    (response) => {
      updateDeal(response.id, {
        status: response.status,
        closedLostReason: response.closedLostReason,
        closedLostReasonText: response.closedLostReasonText
      });
      refreshEntityDetail(queryClient, "deals", response.id);
      queryClient.resetQueries({
        queryKey: ["deals/filter"],
        exact: false,
        type: "all"
      });
      if (response.contractId) {
        queryClient.invalidateQueries({
          queryKey: [
            "contracts/id",
            { pathParams: { contractId: response.contractId } }
          ]
        });
        queryClient.invalidateQueries({ queryKey: ["contracts/filter"] });
      }
    }
  );
};

export interface ClosedLostReasonSuggestion {
  winnerId: number | null;
  justification: string;
  citation: string | null;
  hasUpdate: boolean;
}

export interface ClosedLostReasonAnalysis {
  suggestedByDreamer: true;
  justification: string;
  citation: string | null;
  userNote: string | null;
  suggestedReasonId?: number | null;
}

export const parseClosedLostReasonText = (
  text: string | null | undefined
): { analysis: ClosedLostReasonAnalysis | null; displayText: string | null } => {
  if (!text) return { analysis: null, displayText: null };
  try {
    const parsed = JSON.parse(text);
    if (
      parsed?.suggestedByDreamer === true &&
      typeof parsed.justification === "string" &&
      parsed.justification.length > 0
    ) {
      return {
        analysis: parsed as ClosedLostReasonAnalysis,
        displayText: parsed.userNote ?? null
      };
    }
  } catch {
    // Plain string — user-written note
  }
  return { analysis: null, displayText: text };
};

export const useClosedLostReasonSuggestion = () => {
  return useAPIMutation<
    { pathParams: { dealId: string } },
    ClosedLostReasonSuggestion
  >("deals/closedLostReasonSuggest", { method: "POST" });
};

export interface ClosedLostReasonHistoryEntry {
  closedLostReason: number | null;
  closedLostReasonText: string | null;
  createdAt: string | null;
}

export interface ClosedLostReasonHistoryResponse {
  entries: ClosedLostReasonHistoryEntry[];
}

export const useClosedLostReasonHistory = (dealId: string | undefined) => {
  return useAPI(
    "deals/closedLostReasonHistory",
    { pathParams: { dealId: dealId ?? "" } },
    { enabled: !!dealId }
  );
};

export const useDeleteDealMutation = () => {
  const removeDeal = useBoundStore((state) => state.deals.removeEntity);
  return useAPIMutation<{ pathParams: { dealId: string } }, void>(
    "deals/id",
    {
      method: "DELETE"
    },
    (_response, data) => {
      removeDeal(data.pathParams.dealId);
    }
  );
};

export const useDealsQuestionnairesSettings = (
  stageIds: number[],
  dealId: string | null = null
): {
  data: DealQuestionnaireSettings;
  refetch: () => Promise<void>;
  isLoading: boolean;
} => {
  const sortedStageIds = useMemo(
    () => [...stageIds].sort((a, b) => a - b),
    [stageIds]
  );
  const apiConfigs = useMemo(() => {
    return sortedStageIds.map((stageId) => ({
      endpoint: "settings/questionnaireSettings",
      options: {
        method: "GET",
        pathParams: {
          stageId: String(stageId)
        },
        queryParams: {
          include_additional_info: "true",
          ...(dealId && { deal_id: dealId })
        }
      },
      queryOptions: {
        select: (data: unknown) => data as DealQuestionnaireSettings,
        enabled: stageId > 0,
        staleTime: 0,
        cacheTime: 5 * 60 * 1000,
        refetchOnMount: "always" as const
      }
    }));
  }, [sortedStageIds, dealId]);

  const results = useMultipleAPIs(apiConfigs);

  const refetchAll = async () => {
    await Promise.all(results.map((result) => result.refetch?.()));
  };

  const isLoading = useMemo(
    () => results.some((result) => result.isLoading),
    [results]
  );

  // Create a mapped object of results indexed by stageId
  const data = useMemo(
    () =>
      sortedStageIds.reduce((acc, stageId, index) => {
        acc[stageId] = results[index] as DealsQuestionnairesSettingsResponse;
        return acc;
      }, {} as DealQuestionnaireSettings),
    [sortedStageIds, results]
  );

  return { data, refetch: refetchAll, isLoading };
};

export const useDealsQuestionnairesResponses = (
  dealId: string,
  questionnaireIds: string[]
): {
  data: DealQuestionnaireResults;
  refetch: () => Promise<void>;
  isLoading: boolean;
} => {
  const sortedIds = useMemo(
    () => [...questionnaireIds].sort(),
    [questionnaireIds]
  );
  const apiConfigs = useMemo(
    () =>
      sortedIds.map((questionnaireId) => ({
        endpoint: "deals/questionnaireResponses",
        options: {
          method: "GET",
          pathParams: {
            dealId,
            questionnaireId
          }
        },
        queryOptions: {
          select: (data: any) => data as DealsQuestionnairesResults,
          staleTime: 0,
          cacheTime: 5 * 60 * 1000,
          refetchOnMount: "always" as const,
          enabled: !!dealId && !!questionnaireId
        }
      })),
    [dealId, sortedIds]
  );

  const results = useMultipleAPIs(apiConfigs);

  const refetchAll = async () => {
    await Promise.all(results.map((result) => result.refetch?.()));
  };

  const isLoading = useMemo(
    () => results.some((result) => result.isLoading),
    [results]
  );

  // Create a mapped object of results indexed by stageId
  const data = useMemo(
    () =>
      sortedIds.reduce((acc, id, index) => {
        acc[id] = results[index] as DealsQuestionnairesResultsResponse;
        return acc;
      }, {} as DealQuestionnaireResults),
    [sortedIds, results]
  );

  return { data, refetch: refetchAll, isLoading };
};

export const useUpdateDealQuestionnaireMutation = () => {
  const queryClient = useQueryClient();
  return useAPIMutation<
    {
      pathParams: {
        dealId: string;
        questionnaireId: string;
      };
      data: {
        questions: {
          questionId: string;
          response: {
            value?: QuestionValueType;
            innerValue?: QuestionValueType;
            person?: PersonData;
          };
        }[];
      };
    },
    {
      score: Score;
    }
  >(
    "deals/questionnaireResponses",
    {
      method: "PUT"
    },
    (_response, data) => {
      queryClient.invalidateQueries({
        queryKey: [
          "deals/questionnaireResponses",
          {
            method: "GET",
            pathParams: {
              dealId: data.pathParams.dealId,
              questionnaireId: data.pathParams.questionnaireId
            }
          }
        ]
      });

      queryClient.invalidateQueries({
        queryKey: ["deals/questionnaireScores"],
        predicate: (query) => {
          const queryKey = query.queryKey as any[];
          return (
            queryKey[0] === "deals/questionnaireScores" &&
            queryKey[1]?.pathParams?.dealId === data.pathParams.dealId
          );
        }
      });

      queryClient.invalidateQueries({
        queryKey: ["reports/type"]
      });
    }
  );
};

export const useDeleteQuestionResponseMutation = () => {
  return useAPIMutation<
    {
      pathParams: {
        dealId: string;
        questionnaireId: string;
        questionId: string;
      };
    },
    {
      score: Score;
    }
  >("deals/deleteQuestion", {
    method: "DELETE"
  });
};

export const useRestoreDealQuestionnaireMutation = () => {
  return useAPIMutation<
    {
      pathParams: {
        dealId: string;
        questionnaireId: string;
      };
      data: {
        questionIds: string[];
      };
    },
    {
      successfulCount: number;
      failedCount: number;
      successfulIds: string[];
    }
  >("deals/questionnaireRestore", {
    method: "POST"
  });
};

export const useDealQuestionnairesScores = (
  dealId: string,
  stageIds: number[]
): {
  data: DealQuestionnaireScores;
  isLoading: boolean;
} => {
  const sortedStageIds = useMemo(
    () => [...stageIds].sort((a, b) => a - b),
    [stageIds]
  );
  const apiConfigs = useMemo(() => {
    return sortedStageIds.map((stageId) => ({
      endpoint: "deals/questionnaireScores",
      options: {
        method: "GET",
        pathParams: {
          dealId,
          stageId: String(stageId)
        }
      },
      queryOptions: {
        select: (data: unknown) => data as DealQuestionnaireScores,
        enabled: stageId > 0,
        staleTime: 0,
        cacheTime: 5 * 60 * 1000,
        refetchOnMount: "always" as const
      }
    }));
  }, [sortedStageIds, dealId]);

  const results = useMultipleAPIs(apiConfigs);

  const isLoading = useMemo(
    () => results.some((result) => result.isLoading),
    [results]
  );

  // Create a mapped object of results indexed by stageId
  const data = useMemo(
    () =>
      sortedStageIds.reduce((acc, stageId, index) => {
        acc[stageId] = results[index] as DealsQuestionnairesScoresResponse;
        return acc;
      }, {} as DealQuestionnaireScores),
    [sortedStageIds, results]
  );
  return { data, isLoading };
};

export const useCreateQuestionMutation = () => {
  return useAPIMutation<
    {
      data: CreateQuestionnaireQuestionSettings;
    },
    {
      id: string;
      questionOrder: number;
      questionText: string;
    }
  >("settings/createQuestionnaireQuestion", {
    method: "POST"
  });
};

export const useUpdateQuestionMutation = () => {
  return useAPIMutation<
    {
      pathParams: {
        questionId: string;
      };
      data: Partial<DealQuestionnaireQuestionSettings>;
    },
    {
      question: DealQuestionnaireQuestionSettings;
    }
  >("settings/updateQuestionnaireQuestion", {
    method: "PUT"
  });
};

export const useDeleteQuestionMutation = () => {
  return useAPIMutation<
    {
      pathParams: { questionId: string };
      // The successor is the question taking the deleted one's place: the server moves
      // the deleted question's per-deal answers onto it before retiring it. Required
      // even when empty ({}): useAPIMutation reuses one mutable mutation key per hook
      // instance and only overwrites queryParams when the field is present, so a plain
      // delete after a rename-swap would otherwise silently resend the previous call's
      // successorQuestionId and migrate the field's answers to the wrong question.
      queryParams: { successorQuestionId: string } | Record<string, never>;
    },
    void
  >("settings/updateQuestionnaireQuestion", {
    method: "DELETE"
  });
};

export const useDealQuestionnaireQuestionOptions = (
  questionId: string,
  enabled = true
): DealQuestionnaireQuestionOptionsResponse => {
  const result = useAPI(
    "settings/questionOptions",
    {
      method: "GET",
      pathParams: {
        questionId
      }
    },
    { enabled }
  );
  return result;
};

export const useCreateQuestionnaireOptionMutation = () => {
  return useAPIMutation<
    {
      pathParams: {
        questionId: string;
      };
      data: {
        options: string[];
      };
    },
    {
      successfulCount: number;
      failedCount: number;
      successfulIds: string[];
    }
  >("settings/createQuestionOption", {
    method: "POST"
  });
};

export const useDealNextSteps = (
  dealId: string,
  enabled = true
): DealNextStepsResponse => {
  const result = useAPI(
    "deals/nextSteps",
    {
      method: "GET",
      pathParams: {
        dealId
      }
    },
    { enabled }
  );
  return result;
};

export const useCreateNextStepMutation = () => {
  return useAPIMutation<
    {
      pathParams: { dealId: string };
      data: {
        description: string;
      };
    },
    {
      id: string;
    }
  >("deals/nextSteps", {
    method: "POST"
  });
};

export const useUpdateNextStepMutation = () => {
  return useAPIMutation<
    {
      pathParams: {
        dealId: string;
        nextStepId: string;
      };
      data: {
        entityId?: string;
        description?: string;
      };
    },
    NextStep
  >("deals/updateNextStep", {
    method: "PUT"
  });
};

export const useDeleteNextStepMutation = () => {
  return useAPIMutation<
    { pathParams: { dealId: string; nextStepId: string } },
    void
  >("deals/updateNextStep", {
    method: "DELETE"
  });
};

export const useDealsDistinctFilterOptions = (
  fieldName: string,
  filters?: Filters,
  searchTerm?: string
) => {
  return useDistinctFilterOptions(
    "deals",
    fieldName,
    filters && toDealsRequestFilters(filters),
    searchTerm
  );
};

export const useDealCommercials = (dealId: string, enabled: boolean = true) => {
  return useAPI(
    "deals/commercials",
    {
      method: "GET",
      pathParams: {
        dealId
      }
    },
    { enabled }
  );
};

// dealsapi pairs every new deal with a DRAFT contract at creation time, but the
// create response carries only the deal id — so the pairing has to be read back.
export const useResolveDealContractId = (): ((
  dealId: string
) => Promise<string | null>) => {
  const { token, tenantId, tokenExpiry, login } = useAuth();
  const queryClient = useQueryClient();
  return useCallback(
    async (dealId: string): Promise<string | null> => {
      // Same key shape useAPI builds, so this also primes the entry the deal
      // page's useDealCommercials reads.
      const queryKey: QueryKeyArray = [
        "deals/commercials",
        { method: "GET", pathParams: { dealId } },
        undefined
      ];
      const commercials: Commercials | undefined =
        await queryClient.fetchQuery({
          queryKey,
          queryFn: () => fetchAPIData({ token, tenantId, tokenExpiry }, queryKey),
          // The deal was created moments ago; the client's default stale time
          // would let an earlier cached read stand in for the new pairing.
          staleTime: 0,
          retry: createAuthRetryHandler(login)
        });
      return commercials?.contractId ?? null;
    },
    [queryClient, token, tenantId, tokenExpiry, login]
  );
};

export const useDealCommercialsMutation = () => {
  return useAPIMutation<
    {
      pathParams: {
        dealId: string;
      };
      data: Partial<Commercials>;
    },
    Commercials
  >("deals/commercials", {
    method: "POST"
  });
};

export const useUpdateAlertMutation = () => {
  return useAPIMutation<
    {
      pathParams: {
        dealId: string;
        alertId: string;
      };
      data: {
        isDismissed: boolean;
      };
    },
    DealData
  >("deals/updateAlert", {
    method: "PUT"
  });
};

export const useAlertsBatch = (
  dealIds: string[],
  enabled = true
): ExtendedUseQueryResult<AlertsBatchResponse> => {
  const sortedDealIds = useMemo(() => [...dealIds].sort(), [dealIds]);

  return useAPI(
    "deals/alerts/batch",
    {
      method: "POST",
      data: { dealIds: sortedDealIds }
    },
    { enabled: enabled && sortedDealIds.length > 0 }
  );
};

export const useDealSummary = (
  dealId: string,
  options: { enabled?: boolean } = {}
): DealSummaryQueryResponse => {
  return useAPI(
    "deals/summary",
    {
      pathParams: { dealId },
      method: "GET"
    },
    {
      enabled: options.enabled ?? true
    }
  );
};

export const useQuestionnaireById = (
  questionnaireId: string,
  enabled: boolean = true
): QuestionnaireByIdResponse => {
  return useAPI(
    "questionnaires/byId",
    {
      method: "GET",
      pathParams: { questionnaireId }
    },
    { enabled: enabled && !!questionnaireId }
  );
};

export const useUpdateQuestionnaireMutation = () => {
  return useAPIMutation<
    {
      pathParams: { questionnaireId: string };
      data: QuestionnaireUpdateInput;
    },
    QuestionnaireBaseOutput
  >("questionnaires/update", {
    method: "PUT"
  });
};

export const useReorderQuestionnairesMutation = () => {
  return useAPIMutation<
    {
      pathParams: { stageId: string };
      data: string[]; // Array of questionnaire IDs
    },
    QuestionnaireReorderResponse
  >("questionnaires/reorder", {
    method: "PUT"
  });
};

export const useReorderQuestionnaireQuestionsMutation = () => {
  return useAPIMutation<
    {
      pathParams: { questionnaireId: string };
      data: string[]; // Array of question IDs
    },
    QuestionnaireQuestionsReorderResponse
  >("questionnaires/byId/order", {
    method: "PUT"
  });
};

export const useDeleteQuestionnaireMutation = () => {
  return useAPIMutation<
    {
      pathParams: {
        questionnaireId: string;
      };
    },
    void
  >("questionnaires/delete", {
    method: "DELETE"
  });
};

export const useRestoreQuestionnaireMutation = () => {
  return useAPIMutation<
    {
      pathParams: {
        questionnaireId: string;
      };
    },
    QuestionnaireBaseOutput
  >("questionnaires/restore", {
    method: "POST"
  });
};


export default {
  useDeals,
  useAllDeals,
  useFilteredDeals,
  useResetFilteredDeals,
  useCreateDealMutation,
  useUpdateDealMutation,
  useDealMilestones,
  useUpdateDealStageMutation,
  useAlertsBatch
};
